import React, { useRef, useEffect } from 'react';
import { postApi } from '../../Services/apiService';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../Context/NotificationContext';

const NotificationPanel = () => {
  const {
    dashboardNotifications,
    unreadCount,
    dashboardLoading,
    dashboardHasMore,
    loadMoreDashboardNotifications,
    refreshNotifications
  } = useNotifications();
  
  const navigate = useNavigate();
  const loadMoreTriggerRef = useRef(null);


  // Infinite scroll implementation using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && dashboardHasMore && !dashboardLoading) {
          loadMoreDashboardNotifications();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [dashboardHasMore, dashboardLoading, loadMoreDashboardNotifications]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if it's unread
    if (!notification.isRead) {
      try {
        await postApi(`/cell-remark/${notification.id}/mark-as-read`);
        // Refresh notifications to update counts immediately
        refreshNotifications();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Navigate to the appropriate page based on module
    const params = new URLSearchParams();
    params.append('date', notification.date);
    params.append('cell_type', notification.cell_type);
    
    // Add all cell identification params
    if (notification.product_id) {
      params.append('product_id', notification.product_id);
    }
    if (notification.product_group_id) {
      params.append('product_group_id', notification.product_group_id);
    }
    if (notification.finished_product_id) {
      params.append('finished_product_id', notification.finished_product_id);
    }
    if (notification.raw_product_id) {
      params.append('raw_product_id', notification.raw_product_id);
    }
    if (notification.customer_group_id) {
      params.append('customer_group_id', notification.customer_group_id);
    }
    if (notification.customer_sub_group_id) {
      params.append('customer_sub_group_id', notification.customer_sub_group_id);
    }
    if (notification.reserve_label) {
      params.append('reserve_label', notification.reserve_label);
    }
    if (notification.arrival_index !== null && notification.arrival_index !== undefined) {
      params.append('arrival_index', notification.arrival_index);
    }
    
    // Determine navigation path based on module
    const navigationPath = notification.module === 'inout' 
      ? '/admin/inout' 
      : '/admin/prepack-inout';
    
    navigate(`${navigationPath}?${params.toString()}`);
  };

  const getCellTypeLabel = (cellType) => {
    const labels = {
      'arrival_info': 'Arrival Info',
      'sales_reserve': 'Sales Reserve',
      'arrival_qty': 'Arrival Qty',
      'total_qty': 'Total Qty',
      'estimate_stock': 'Estimate Stock',
      'convert_in': 'Convert In',
      'convert_out': 'Convert Out',
    };
    return labels[cellType] || cellType;
  };

  const getProductDisplay = (notification) => {
    // For product groups (both inout and prepack modules)
    if (notification.product_group) {
      return `Group: ${notification.product_group.group_no} ${notification.product_group.name || ''}`;
    }
    // For products (both inout and prepack modules)
    if (notification.product) {
      return `${notification.product.code} - ${notification.product.name_tc}`;
    } 
    
  };

  return (
    <div className="notification-panel-container" style={{
      background: '#fff',
      borderRadius: '10px',
      padding: '15px',
      height: 'calc(100vh - 150px)',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        paddingBottom: '10px',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#006E47' }}>
          Live Notifications
        </h4>
        
        <span style={{ fontSize: '12px', color: '#666' }}>
          {unreadCount} unread
        </span>
      </div>

 
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {dashboardLoading && dashboardNotifications.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px', 
            color: '#999',
            fontSize: '14px'
          }}>
            Loading...
          </div>
        ) : dashboardNotifications.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px', 
            color: '#999',
            fontSize: '14px'
          }}>
            No remarks
          </div>
        ) : (
          <>
            {dashboardNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                style={{
                  padding: '10px',
                  marginBottom: '8px',
                  borderRadius: '6px',
                  background: !notification.isRead ? '#f0f9ff' : '#ffffff',
                  border: `1px solid ${!notification.isRead ? '#e0f2fe' : '#f0f0f0'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                  <span style={{
                    fontWeight: 600,
                    color: '#006E47',
                    fontSize: '11px'
                  }}>
                    {getCellTypeLabel(notification.cell_type)}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    color: '#999'
                  }}>
                    {notification.date}
                  </span>
                </div>
                <div style={{
                  fontWeight: 500,
                  marginBottom: '4px',
                  fontSize: '11px',
                  color: '#333'
                }}>
                  {getProductDisplay(notification)}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '11px',
                  lineHeight: '1.4',
                  marginBottom: '4px'
                }}>
                  {notification.remark.substring(0, 80)}
                  {notification.remark.length > 80 && '...'}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '10px',
                  color: '#999'
                }}>
                  <span>By: {notification.creator?.name}</span>
                  <span>
                    {new Date(notification.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Infinite Scroll Trigger */}
            {dashboardHasMore && (
              <div 
                ref={loadMoreTriggerRef} 
                style={{ padding: '10px', textAlign: 'center' }}
              >
                {dashboardLoading && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    Loading more...
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
 
    </div>
  );
};

export default NotificationPanel;
